import React, { useMemo } from "react";
import zxcvbn from "zxcvbn";

/**
 * Password strength meter powered by zxcvbn.
 *
 * Renders a 4-segment bar (red → amber → lime → emerald), a label, and up to
 * two of zxcvbn's targeted suggestions. Purely a UX guide — the backend still
 * enforces its own 8-character minimum.
 */
const LABELS = ["Very weak", "Weak", "Okay", "Strong", "Very strong"];
const COLORS = [
    "bg-rose-500",       // score 0
    "bg-orange-500",     // score 1
    "bg-amber-500",      // score 2
    "bg-lime-500",       // score 3
    "bg-emerald-500",    // score 4
];
const TEXT_TONES = [
    "text-rose-600 dark:text-rose-400",
    "text-orange-600 dark:text-orange-400",
    "text-amber-600 dark:text-amber-400",
    "text-lime-600 dark:text-lime-400",
    "text-emerald-600 dark:text-emerald-400",
];

export default function PasswordStrengthMeter({ password = "", userInputs = [], testId = "password-strength-meter" }) {
    const result = useMemo(() => {
        if (!password) return null;
        // Cap the input length so zxcvbn stays snappy on ridiculous inputs.
        return zxcvbn(password.slice(0, 100), userInputs.filter(Boolean));
    }, [password, userInputs]);

    if (!password) {
        return (
            <div data-testid={`${testId}-empty`} className="mt-2 flex gap-1" aria-hidden="true">
                {[0, 1, 2, 3].map((i) => (
                    <div key={i} className="h-1 flex-1 rounded-full bg-muted" />
                ))}
            </div>
        );
    }

    const score = result?.score ?? 0;
    const label = LABELS[score];
    // zxcvbn returns { warning, suggestions[] }. Show at most 2 practical hints.
    const feedback = result?.feedback || {};
    const hints = [feedback.warning, ...(feedback.suggestions || [])]
        .filter((v) => typeof v === "string" && v.trim().length > 0)
        .slice(0, 2);

    return (
        <div data-testid={testId} className="mt-2 space-y-1.5">
            <div className="flex gap-1" aria-hidden="true">
                {[0, 1, 2, 3].map((i) => (
                    <div
                        key={i}
                        className={`h-1 flex-1 rounded-full transition-colors ${
                            i <= score - 1 || (score === 0 && i === 0) ? COLORS[score] : "bg-muted"
                        }`}
                    />
                ))}
            </div>
            <div className="flex items-center justify-between text-[11px]">
                <span
                    data-testid={`${testId}-label`}
                    className={`font-medium ${TEXT_TONES[score]}`}
                >
                    {label}
                </span>
                {password.length < 8 && (
                    <span className="text-muted-foreground">
                        {password.length}/8 chars minimum
                    </span>
                )}
            </div>
            {hints.length > 0 && (
                <ul data-testid={`${testId}-hints`} className="text-[11px] text-muted-foreground list-disc pl-4 leading-relaxed">
                    {hints.map((h, i) => (
                        <li key={i}>{h}</li>
                    ))}
                </ul>
            )}
        </div>
    );
}
